import { useNavigator } from "@/contexts/navigator/NavigatorContext";
import { LearnerOutcome } from "@/lib/models";
import { LearnerOutcomeDetails } from "./LearnerOutcomeDetails";
import Image from "next/image";
import { IllustratedBanner } from "./IllustratedBanner";
import colors from "@/lib/static/colors";
import { CompetencyDetailSidePanel } from "./CompetencySidePanel";
import { CompetencyDetails } from "./CompetencyDetails";
import { useEntities } from "@/contexts/entities/EntitiesContext";

type LearnerOutcomeTabProps = {
  learnerOutcome: LearnerOutcome;
  activeLearnerOutcomeId: string | null;
  changeSelection: (learnerOutcomeId: string) => Promise<void>;
};

export const LearnerOutcomeTab = ({
  learnerOutcome,
  activeLearnerOutcomeId,
  changeSelection,
}: LearnerOutcomeTabProps) => {
  const isSelected = learnerOutcome.id == activeLearnerOutcomeId;

  const defaultStyles =
    "bg-white border-tab-border hover:bg-neutral-100 active:bg-tab-bg-press";
  const selectedStyles = `bg-${learnerOutcome.id}-20 border-${learnerOutcome.id}-50`;

  return (
    <button
      key={learnerOutcome.name}
      aria-label={learnerOutcome.name}
      className={`flex h-16 w-[242px] items-center justify-start whitespace-normal rounded-full border p-0 ${
        isSelected ? selectedStyles : defaultStyles
      }`}
      onClick={() => changeSelection(learnerOutcome.id)}
    >
      <div className="my-2 ml-4 h-[48px] w-[42px]">
        {learnerOutcome.learnerOutcomeCard.sourceUrl && (
          <Image
            src={learnerOutcome.learnerOutcomeCard.sourceUrl}
            alt=""
            width={42}
            height={48}
            className="h-[48px] w-[42px]"
          />
        )}
      </div>
      <div className="ml-3 w-[169px] text-left">
        <div className="font-roobert text-[6px] font-medium uppercase text-black">
          Learner Outcome
        </div>
        <div className="font-roobert text-sm font-medium leading-none text-black">
          {learnerOutcome.name}
        </div>
      </div>
    </button>
  );
};

type LearnerOutcomeTabPanelProps = {
  learnerOutcome: LearnerOutcome;
  changeCompetency: (competencyId: string) => Promise<void>;
};

export const LearnerOutcomeTabPanel = ({
  learnerOutcome,
  changeCompetency,
}: LearnerOutcomeTabPanelProps) => {
  const { activeCompetencyId } = useNavigator();
  const { getCompetency } = useEntities();

  const activeCompetency = getCompetency(activeCompetencyId ?? "");

  const bannerFill = colors[`${learnerOutcome.id}-40`];

  return (
    <div className="relative pb-10 medium:p-6 medium:pb-10">
      <div
        className={`${
          activeCompetencyId
            ? "h-[436px] medium:h-[265px] large:h-[340px]"
            : "h-[580px] large:h-[620px]"
        } bg-${
          learnerOutcome.id
        }-10 absolute right-0 top-0 z-0 w-full overflow-hidden border-b border-stone-200`}
      >
        <IllustratedBanner fill={bannerFill} className="mx-auto h-[620px]" />
      </div>

      <LearnerOutcomeDetails
        learnerOutcome={learnerOutcome}
        activeCompetency={activeCompetency}
      />
      <div
        className={`${
          !activeCompetency ? "medium:hidden" : ""
        } relative z-10 grid w-full max-w-screen-large grid-cols-4 gap-4 small:grid-cols-8 medium:grid-cols-12 large:mx-auto large:mt-6 large:gap-6`}
      >
        <CompetencyDetailSidePanel
          learnerOutcome={learnerOutcome}
          activeCompetency={activeCompetency}
          changeCompetency={changeCompetency}
        />
        <CompetencyDetails
          className={`${
            !activeCompetency ? "hidden" : ""
          } col-span-full mx-4 rounded-r-sm pt-6 medium:col-span-9 medium:mx-0`}
          competency={activeCompetency}
          changeCompetency={changeCompetency}
          learnerOutcome={learnerOutcome}
        />
      </div>
    </div>
  );
};
